import React, { useState, useEffect } from 'react';
import axios from '../config/axiosConfig';
import { API_ENDPOINTS } from '../config/api';
import { buildBillPrintHtml } from '../utils/printBillHtml';
import BillPrintFrameModal from './BillPrintFrameModal';

/**
 * Bill preview in a modal (Close + Print). Tries the receipt printer API first;
 * browser path opens an in-app iframe full-screen on mobile, then optional system print.
 */
const BillPreviewModal = ({
    isOpen,
    onClose,
    order,
    orderId: orderIdProp,
    tryServerPrint = true,
    onNotify,
    restaurantName,
}) => {
    const [isPrinting, setIsPrinting] = useState(false);
    const [printFrameHtml, setPrintFrameHtml] = useState(null);

    const orderId = order?._id != null ? orderIdProp ?? order._id : orderIdProp;

    useEffect(() => {
        if (!isOpen) setPrintFrameHtml(null);
    }, [isOpen]);

    if (!isOpen || !order) return null;

    const shortId = orderId?.toString().slice(-6) ?? '';

    const notify = (message, type = 'info') => {
        if (onNotify) onNotify(message, type);
    };

    const openInAppPrintPreview = () => {
        const html = buildBillPrintHtml(order, orderId);
        if (!html) return;
        setPrintFrameHtml(html);
        notify('Bill opened below. Use “Print / Save PDF” if you need a copy.', 'info');
    };

    const handlePrint = async () => {
        if (!order) return;
        setIsPrinting(true);
        try {
            if (tryServerPrint && orderId) {
                try {
                    await axios.get(API_ENDPOINTS.ORDER_PRINT_BILL(orderId));
                    notify('Bill sent to printer', 'success');
                    onClose?.();
                    return;
                } catch (err) {
                    notify(
                        err.response?.data?.message || 'Printer unavailable. Showing bill here instead.',
                        'warning'
                    );
                }
            }
            openInAppPrintPreview();
        } finally {
            setIsPrinting(false);
        }
    };

    const tableLabel =
        order.tableId?.tableNumber != null
            ? `Table ${order.tableId.tableNumber}`
            : order.tableId?.displayName || order.tableId?.tableName || null;

    return (
        <>
        {printFrameHtml && (
            <BillPrintFrameModal html={printFrameHtml} onClose={() => setPrintFrameHtml(null)} />
        )}
        <div
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4"
            role="dialog"
            aria-modal="true"
            aria-labelledby="bill-preview-title"
            onClick={(e) => e.target === e.currentTarget && !isPrinting && onClose?.()}
        >
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-hidden flex flex-col">
                <div className="p-4 border-b border-gray-200 flex justify-between items-center flex-shrink-0">
                    <div>
                        <h3 id="bill-preview-title" className="text-lg font-bold text-gray-900">
                            Bill Preview
                        </h3>
                        {restaurantName && (
                            <p className="text-xs text-gray-500 mt-0.5">{restaurantName}</p>
                        )}
                    </div>
                    <button
                        type="button"
                        onClick={() => !isPrinting && onClose?.()}
                        className="text-gray-400 hover:text-gray-600 text-2xl leading-none p-1 rounded hover:bg-gray-100"
                        aria-label="Close"
                    >
                        ×
                    </button>
                </div>

                <div className="p-4 overflow-y-auto flex-1 text-gray-800">
                    <p className="text-sm text-gray-500 mb-1">Order #{shortId}</p>
                    <p className="font-semibold text-gray-900">{order.customerName || 'Guest'}</p>
                    <p className="text-sm text-gray-600">{order.mobileNumber || '—'}</p>
                    {tableLabel && (
                        <p className="text-sm text-gray-600">{tableLabel}</p>
                    )}
                    <p className="text-sm text-gray-500 mb-3">
                        {order.orderDate ? new Date(order.orderDate).toLocaleString() : ''}
                    </p>

                    <table className="w-full text-sm border-collapse">
                        <thead>
                            <tr className="border-b border-gray-200">
                                <th className="text-left py-2 font-semibold">Item</th>
                                <th className="text-right py-2 font-semibold">Qty</th>
                                <th className="text-right py-2 font-semibold">Price</th>
                                <th className="text-right py-2 font-semibold">Amount</th>
                            </tr>
                        </thead>
                        <tbody>
                            {(order.items || []).map((item, idx) => (
                                <tr key={idx} className="border-b border-gray-100">
                                    <td className="py-2">{item.name}</td>
                                    <td className="text-right">{item.quantity || 0}</td>
                                    <td className="text-right">₹{(Number(item.price) || 0).toFixed(2)}</td>
                                    <td className="text-right">
                                        ₹
                                        {(
                                            (Number(item.price) || 0) * (Number(item.quantity) || 0)
                                        ).toFixed(2)}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>

                    <div className="mt-3 text-sm text-right space-y-1">
                        <div>Subtotal: ₹{(Number(order.totalAmount) || 0).toFixed(2)}</div>
                        {(Number(order.taxAmount) || 0) > 0 && (
                            <div>Tax: ₹{(Number(order.taxAmount) || 0).toFixed(2)}</div>
                        )}
                        {(Number(order.serviceAmount) || 0) > 0 && (
                            <div>Service: ₹{(Number(order.serviceAmount) || 0).toFixed(2)}</div>
                        )}
                        {(Number(order.offerDiscount) || 0) > 0 && (
                            <div>Discount: −₹{(Number(order.offerDiscount) || 0).toFixed(2)}</div>
                        )}
                        <div className="font-bold text-lg text-green-700 mt-2">
                            Total: ₹{(Number(order.finalAmount) || 0).toFixed(2)}
                        </div>
                    </div>
                </div>

                <div className="p-4 border-t border-gray-200 flex gap-2 justify-stretch sm:justify-end flex-shrink-0 bg-white">
                    <button
                        type="button"
                        onClick={() => !isPrinting && onClose?.()}
                        disabled={isPrinting}
                        className="w-full sm:w-auto px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 font-medium disabled:opacity-60"
                    >
                        Close
                    </button>
                    <button
                        type="button"
                        onClick={handlePrint}
                        disabled={isPrinting}
                        className="hidden sm:inline-flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium disabled:opacity-60"
                    >
                        {isPrinting ? (
                            <>
                                <span className="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                Printing…
                            </>
                        ) : (
                            'Print'
                        )}
                    </button>
                </div>
            </div>
        </div>
        </>
    );
};

export default BillPreviewModal;
